import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CandidateNav } from "@/components/CandidateNav";
import { AdminSwitcher } from "@/components/AdminSwitcher";

import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pending from "@/pages/Pending";
import Admin from "@/pages/Admin";
import UploadCV from "@/pages/candidate/UploadCV";
import CandidateHome from "@/pages/candidate/Home";
import CandidateProfile from "@/pages/candidate/Profile";
import CandidateJobs from "@/pages/candidate/Jobs";
import CandidateCompanies from "@/pages/candidate/Companies";
import CandidateCompanyDetail from "@/pages/candidate/CompanyDetail";
import { CandidateHeader } from "@/components/CandidateHeader";
import ShareProfile from "@/pages/candidate/Share";
import PublicProfile from "@/pages/PublicProfile";
import SharedCandidate from "@/pages/SharedCandidate";
import Consent from "@/pages/Consent";
import CompanyDashboard from "@/pages/company/Dashboard";
import UploadCandidate from "@/pages/company/UploadCandidate";
import Database from "@/pages/company/Database";
import FindCandidate from "@/pages/company/FindCandidate";
import SearchCandidate from "@/pages/company/SearchCandidate";
import CandidateView from "@/pages/company/CandidateView";
import CompanySettings from "@/pages/company/Settings";
import CompanyAudit from "@/pages/company/Audit";
import GdprDashboard from "@/pages/company/GdprDashboard";
import Jobs from "@/pages/company/Jobs";
import JobEditor from "@/pages/company/JobEditor";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Načítavam…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = user.role === "admin";
  // Admin has full access to candidate, company AND admin zones.
  if (role && user.role !== role && !isAdmin) {
    return <Navigate to={user.role === "company" ? "/company" : "/app/home"} replace />;
  }
  if (!isAdmin && user.status !== "approved") return <Navigate to="/pending" replace />;
  return children;
}

function CandidateShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <CandidateHeader />
      <div className="mx-auto w-full max-w-md min-h-screen bg-[#F8FAFC] pb-24 relative">
        {children}
        <CandidateNav />
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <AdminSwitcher />
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pending" element={<Pending />} />
            <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
            <Route path="/p/:slug" element={<PublicProfile />} />
            <Route path="/shared/candidate/:token" element={<SharedCandidate />} />
            <Route path="/consent/:token" element={<Consent />} />

            <Route path="/app/upload" element={<Protected role="candidate"><UploadCV /></Protected>} />
            <Route path="/app/home" element={<Protected role="candidate"><CandidateShell><CandidateHome /></CandidateShell></Protected>} />
            <Route path="/app/profile" element={<Protected role="candidate"><CandidateShell><CandidateProfile /></CandidateShell></Protected>} />
            <Route path="/app/jobs" element={<Protected role="candidate"><CandidateJobs /></Protected>} />
            <Route path="/app/companies" element={<Protected role="candidate"><CandidateCompanies /></Protected>} />
            <Route path="/app/companies/:id" element={<Protected role="candidate"><CandidateCompanyDetail /></Protected>} />
            <Route path="/app/share" element={<Protected role="candidate"><CandidateShell><ShareProfile /></CandidateShell></Protected>} />

            <Route path="/company" element={<Protected role="company"><CompanyDashboard /></Protected>} />
            <Route path="/company/upload" element={<Protected role="company"><UploadCandidate /></Protected>} />
            <Route path="/company/database" element={<Protected role="company"><Database /></Protected>} />
            <Route path="/company/database/list" element={<Protected role="company"><Database /></Protected>} />
            <Route path="/company/find" element={<Protected role="company"><FindCandidate /></Protected>} />
            <Route path="/company/search" element={<Protected role="company"><SearchCandidate /></Protected>} />
            <Route path="/company/jobs" element={<Protected role="company"><Jobs /></Protected>} />
            <Route path="/company/jobs/new" element={<Protected role="company"><JobEditor /></Protected>} />
            <Route path="/company/jobs/:id" element={<Protected role="company"><JobEditor /></Protected>} />
            <Route path="/company/candidate/:id" element={<Protected role="company"><CandidateView /></Protected>} />
            <Route path="/company/settings" element={<Protected role="company"><CompanySettings /></Protected>} />
            <Route path="/company/gdpr" element={<Protected role="company"><GdprDashboard /></Protected>} />
            <Route path="/company/audit" element={<Protected role="company"><CompanyAudit /></Protected>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
